import { useState } from 'react';
import { AlertTriangle, Settings } from 'lucide-react';
import { FormSection } from '../../../types/formBuilder';
import { getVariableNameFromField } from '../../../utils/formPreviewHelpers';
import { FormFieldRenderer } from './FormFieldRenderer';

interface FormPreviewFieldsProps {
  sections: FormSection[];
  formData: Record<string, any>;
  formErrors: Record<string, string>;
  disabled: boolean;
  onInputChange: (fieldId: string, value: any) => void;
}

export const FormPreviewFields = ({
  sections,
  formData,
  formErrors,
  disabled,
  onInputChange
}: FormPreviewFieldsProps) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Separate basic and advanced fields
  const basicFields = sections.flatMap(section => 
    section.fields.filter(field => !field.advanced && !field.hidden).map(field => ({ ...field, sectionTitle: section.title }))
  );
  
  const advancedFields = sections.flatMap(section => 
    section.fields.filter(field => field.advanced && !field.hidden).map(field => ({ ...field, sectionTitle: section.title }))
  );

  const renderField = (field: any) => {
    const variableName = getVariableNameFromField(field);
    const hasError = !!formErrors[variableName];
    
    return (
      <div key={field.id} className="space-y-2">
        {field.label && field.type !== 'boolean' && (
          <label className="block text-sm font-medium text-th-primary">
            {field.label}
            {field.required && <span className="text-status-error ml-1">*</span>}
          </label>
        )}
        
        <FormFieldRenderer
          field={field}
          value={formData[variableName] ?? field.defaultValue ?? ''}
          hasError={hasError}
          disabled={disabled}
          onChange={(value) => onInputChange(field.id, value)}
        />
        
        {hasError && (
          <p className="text-status-error text-xs mt-1 flex items-center space-x-1">
            <AlertTriangle className="w-3 h-3" />
            <span>{hasError}</span>
          </p>
        )}
        
        {field.description && (
          <p className="text-xs text-th-secondary mt-1">
            {field.description}
          </p>
        )}
        
        {/* Debug info in development */}
        {import.meta.env.MODE === 'development' && (
          <p className="text-xs text-th-secondary/50 font-mono">
            API key: {variableName}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Debug Info */}
      {import.meta.env.MODE === 'development' && (
        <div className="bg-elevated/50 border border-subtle/30 rounded p-2 text-xs text-th-secondary">
          <p>Debug: {sections.length} sections, {sections.flatMap(s => s.fields).length} total fields</p>
        </div>
      )}

      {/* Basic Fields */}
      {basicFields.length > 0 && (
        <div className="space-y-6">
          {basicFields.map(field => renderField(field))}
        </div>
      )}

      {/* Advanced Fields Toggle */}
      {advancedFields.length > 0 && (
        <div className="pt-4">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center space-x-2 text-th-secondary hover:text-brand transition-colors"
          >
            <Settings className="w-4 h-4" />
            <span className="text-sm">Advanced Settings ({advancedFields.length})</span>
          </button>

          {showAdvanced && (
            <div className="mt-4 pt-4 border-t border-subtle/30 space-y-6">
              {advancedFields.map(field => renderField(field))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};